import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core'
import { ParkingService } from '../../../services/parking.service'
import { ActivatedRoute, Router } from '@angular/router'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import * as moment from 'moment'
import {
    Booking,
    bookingsPath,
    Client,
    clientsPath,
    Image as ParkUppImage,
    MerchantTenant,
    merchantTenantsPath,
    Occupant,
    occupantsPath,
    Parking,
    parkingsPath,
    ParkingType,
    Plan,
    Subscription,
    subscriptionsPath,
    TrackingEntryType,
} from '@parkupp/core'
import { SubscriptionService } from '../../../services/subscription.service'
import { AuthenticationService } from '../../../services/authentication.service'
import { addDoc, collection, collectionData, doc, docData, getDocs, orderBy, query, serverTimestamp, where } from '@angular/fire/firestore'
import { ClientService } from '../../../services/client.service'
import { SortImagesByDatePipe } from '../../core/pipes/sort-images-by-date.pipe'
import { PaymentService } from '../../../services/payment.service'
import { ToastrService } from 'ngx-toastr'
import { Meta, Title } from '@angular/platform-browser'
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal'
import * as firestore from 'firebase/firestore'
import { SortImagesByPrimaryImagePipe } from '../../core/pipes/sort-images-by-primary-image.pipe'
import { OmnisendService } from '../../../services/omnisend.service'
import { GaActionEnum, GoogleAnalyticsService } from 'ngx-google-analytics'
import { GlobalService } from '../../../services/global.service'
import { TrackingService } from '../../../services/tracking.service'
import { BookingService } from '../../../services/booking.service'
import { environment } from '../../../../environments/environment'

@Component({
    selector: 'app-parking-page-bookupp',
    templateUrl: './parking-page-bookupp.component.html',
    styleUrls: ['./parking-page-bookupp.component.scss'],
})
export class ParkingPageBookuppComponent implements OnInit {
    parkingKey: string
    parking: Parking

    formData: FormGroup
    formLoading: boolean = false
    formError: string
    selectedPlan: Plan
    openPopup: boolean = false
    subscriptionReference: string = ''
    subscriptions: Subscription[]
    images: ParkUppImage[] = []
    highestIndex = 0
    currentImageIndex = 1
    showSendRequest = false
    typeSpecificAmenities: any = {}

    // Occupants
    @ViewChild('popupOccupant') popupOccupant: TemplateRef<any>
    modalRefOccupant?: BsModalRef | null
    formDataOccupant: FormGroup
    formLoadingOccupant: boolean = false
    formErrorOccupant: string

    occupants: Occupant[]
    occupantLimit: number = 1

    showNumberOfBaysLeft: boolean = false
    showContractDuration: boolean = true
    showNumberOfBaysSelected: boolean = true
    showSelectOccupants: boolean = false
    showPlanSelector = false

    totalDailySavings: number

    bookingMinDate: string
    bookingMaxDate: string
    bookingAreas: any
    modalRef: BsModalRef<unknown>

    protected readonly event = event
    bookings: Booking[]
    accessDenied = false
    parkingLayoutImage: string
    public svgWidth: number
    public svgHeight: number

    constructor(
        private toastrService: ToastrService,
        private parkingService: ParkingService,
        private paymentService: PaymentService,
        private clientService: ClientService,
        private router: Router,
        private subscriptionService: SubscriptionService,
        private authenticationService: AuthenticationService,
        private activatedRoute: ActivatedRoute,
        private titleService: Title,
        private metaTagService: Meta,
        private modalService: BsModalService,
        private omnisendService: OmnisendService,
        private googleAnalyticsService: GoogleAnalyticsService,
        public globalService: GlobalService,
        private trackingService: TrackingService,
        private bookingService: BookingService
    ) {
        this.parkingKey = this.activatedRoute.snapshot.params['parkingKey']
        // this.selectedPlan = new Plan()

        docData(doc(this.parkingService.firestore, parkingsPath(), this.parkingKey)).subscribe((documentData: any) => {
            this.parking = new Parking(documentData)
            this.parking.$key = this.parkingKey

            // Meta Data
            this.titleService.setTitle(`Parking Available: ${this.parking.title} in ${this.parking.parkingLocation.shortAddress}`)
            this.metaTagService.updateTag({ name: 'description', content: this.parking.metaDescription! })

            if (this.parking.merchantRef) {
                // const refMerchantTenants = query(collection(this.clientService.firestore, merchantTenantsPath(this.parking.merchantRef.id as string)))
                const refMerchantTenants = collection(this.clientService.firestore, merchantTenantsPath(this.parking.merchantRef.id as string))
                getDocs(refMerchantTenants).then((snapshot) => {
                    const tenants = snapshot.docs.map((doc) => {
                        let tenant = new MerchantTenant(doc.data())
                        tenant.$key = doc.id
                        return tenant
                    })

                    this.clientService.activeClient$.subscribe((activeClient) => {
                        const loggedInUid = this.authenticationService.uid
                        const tenant = tenants.find((item) => item.uid === loggedInUid)

                        if (!tenant || !tenant.active) {
                            if (this.authenticationService.isAnonymous) {
                                const currentUrl = this.router.url
                                this.router.navigate(['/sign-in'], { queryParams: { returnUrl: currentUrl } })
                            } else {
                                this.accessDenied = true
                            }
                        }
                    })
                })
            }

            this.images = []
            if (this.parking.images) {
                for (const key in this.parking.images) {
                    this.images.push(this.parking.images[key])
                }
                this.images = SortImagesByDatePipe.sortImages(this.images)
                this.images = SortImagesByPrimaryImagePipe.sortImages(this.images, this.parking.imagePrimary ? this.parking.imagePrimary : '')
            }

            this.drawBookingAreas()

            this.getImageDimensions(this.parking.parkingLayoutImage)
        })

        this.formData = new FormGroup({
            startDate: new FormControl(moment().format('yyyy-MM-DD'), [Validators.required]),
            bayNumber: new FormControl('', Validators.required),
            occupants: new FormControl('', Validators.required),
        })
        const currentDate = new Date()
        this.bookingMinDate = moment(currentDate).format('YYYY-MM-DD')
        this.bookingMaxDate = moment(currentDate).add(7, 'days').format('YYYY-MM-DD')

        this.formData.controls['startDate'].valueChanges.subscribe((value) => this.formChanges({ startDate: value }))

        // Occupant
        this.formDataOccupant = new FormGroup({
            numberPlate: new FormControl('', Validators.required),
            model: new FormControl('', Validators.required),
            colour: new FormControl('', Validators.required),
            fullName: new FormControl(''),
            description: new FormControl(''),
        })
    }

    ngOnInit(): void {
        this.clientService.activeClient$.subscribe((activeClient) => {
            // TODO: Mark - this is firing twice
            if (activeClient && activeClient.$key) {
                const parkingRef = doc(this.parkingService.firestore, parkingsPath(), this.parkingKey)
                const clientRef = doc(this.parkingService.firestore, clientsPath(), activeClient.$key)

                // Get current bookings
                const today = new Date()
                today.setHours(0, 0, 0, 0) // Set to the start of today

                collectionData(
                    query(
                        collection(this.parkingService.firestore, bookingsPath()),
                        orderBy('createdAt'),
                        where('clientRef', '==', clientRef),
                        where('createdAt', '>=', today) // Add this line to filter for today and future dates
                    ),
                    { idField: 'documentId' }
                ).subscribe((documents: any[]) => {
                    this.bookings = []
                    documents.forEach((documentData) => {
                        let booking = new Booking(documentData)
                        booking.$key = documentData.documentId

                        docData(documentData.parkingRef).subscribe((data) => {
                            booking.$parking = new Parking(data)
                            booking.$parking.$key = documentData.parkingRef.id
                        })

                        this.bookings.push(booking)
                    })
                })

                const ref = query(
                    collection(this.clientService.firestore, subscriptionsPath()),
                    where('paymentInitiated', '==', true),
                    where('clientRef', '==', clientRef),
                    where('parkingRef', '==', parkingRef),
                    orderBy('createdAt', 'desc')
                )
                collectionData(ref, { idField: 'documentId' }).subscribe((documents: any[]) => {
                    this.subscriptions = []
                    documents.forEach((documentData) => {
                        const subscription = new Subscription(documentData)
                        subscription.$key = documentData.documentId

                        docData(documentData.parkingRef).subscribe((data) => {
                            subscription.$parking = data as Parking
                        })
                        docData(documentData.clientRef).subscribe((data) => {
                            subscription.$client = data as Client
                        })
                        this.subscriptions.push(subscription)
                    })
                })

                const refOccupants = query(collection(this.clientService.firestore, occupantsPath(this.clientService.activeClient?.$key as string)))
                collectionData(refOccupants, { idField: 'documentId' }).subscribe((documents: any[]) => {
                    // get occupants
                    this.occupants = []
                    documents.forEach((documentData) => {
                        const occupant = new Occupant(documentData)
                        occupant.$key = documentData.documentId

                        if (!occupant.deletedAt) {
                            this.occupants.push(occupant)
                        }

                        // Set all
                        const occupantMap = this.occupants.map((occupant) => occupant.$key)
                        this.formData.controls['occupants'].setValue(occupantMap)
                    })
                })
            }

            // Tracking
            const parkingRef = doc(this.parkingService.firestore, parkingsPath(), this.parkingKey)
            const userId = this.authenticationService.uid
            this.trackingService.addEntry(TrackingEntryType.VIEW, parkingRef, userId)
        })
    }

    toggleSendRequest() {
        this.showSendRequest = !this.showSendRequest
    }

    formChanges(changes: { baysBooked?: number; contractLength?: number; planTerm?: string; startDate?: string }) {
        let term = changes.planTerm ? changes.planTerm : this.formData.value.planTerm
        let bays = changes.baysBooked ? changes.baysBooked : this.formData.value.baysBooked
        let contractLength = changes.contractLength ? changes.contractLength : this.formData.value.contractLength
        let startDate = changes.startDate ? changes.startDate : this.formData.value.startDate

        if (this.parking.plans && changes.planTerm) {
            console.log(this.parking.plans)
            this.selectedPlan = this.parking.plans.find((i) => i.term === term) as Plan
            console.log(this.selectedPlan)
        }

        // this.totalDailySavings = this.parking.dailySavings * contractLength * 30.5

        this.occupantLimit = bays
    }

    activeSlideChange(index: number) {
        this.currentImageIndex = index + 1
        if (index > this.highestIndex) {
            this.highestIndex = index
        }
    }

    openModalOccupant() {
        if (!this.authenticationService.isAnonymous) {
            this.modalRefOccupant = this.modalService.show(this.popupOccupant, { backdrop: 'static' })
            return false
        } else {
            this.router.navigate([`/sign-up`], {
                queryParams: { returnUrl: this.router.url },
            })
            return
        }
    }

    createOccupant() {
        // if (this.formDataOccupant.value.fullName == '' && this.formDataOccupant.value.numberPlate == '') {
        //     this.toastrService.error('You must provide either a driver name or a number plate.')
        //     return
        // }

        this.formLoading = false

        const occupant = new Occupant({
            fullName: this.formDataOccupant.value.fullName,
            model: this.formDataOccupant.value.model,
            colour: this.formDataOccupant.value.colour,
            registration: this.formDataOccupant.value.numberPlate,
            description: this.formDataOccupant.value.description,
            createdAt: serverTimestamp() as firestore.Timestamp,
            updatedAt: serverTimestamp() as firestore.Timestamp,
        })

        this.authenticationService.waitForUid().then(() => {
            addDoc(collection(this.parkingService.firestore, occupantsPath(this.clientService.activeClient?.$key as string)), occupant.parse()).then((docRef) => {
                this.closeModalOccupant()
                console.log({ status: 'success', occupantsRef: docRef.id })

                // Select that option
                const currentOccupants = this.formData?.get('occupants')?.value || []
                currentOccupants.push(docRef.id)
                this.formData.get('occupants')?.setValue(currentOccupants)
            })
        })
    }

    closeModalOccupant() {
        // this.modalService.hide()
        this.modalRefOccupant?.hide()
        // this.onClosed.emit()
    }

    readonly ParkingType = ParkingType

    areaClicked(area: any): void {
        // console.log(`Area ${area.bayNumber} clicked!`)
        // Add your logic here
        if (!area.unavailable) {
            this.formData.controls['bayNumber']?.setValue(area.bayNumber)
            this.modalRef?.hide()
        } else {
            this.formData.controls['bayNumber']?.setValue('')
        }
    }

    protected readonly onabort = onabort

    highlightArea(event: Event): void {
        // const target = event.target as SVGElement
        // target.style.fill = 'rgba(114,255,60,0.8)'
    }

    resetArea(event: Event): void {
        // const target = event.target as SVGElement
        // target.style.fill = 'rgba(64,131,38,0.5)'
    }

    onDayChanged() {
        // TODO: If bay number is not available
        this.formData.controls['bayNumber']?.setValue('')

        // console.log(this.parking)

        const date = this.formData.controls['startDate'].value
        this.drawBookingAreas(date)
    }

    createBooking(event: any) {
        event.preventDefault()
        this.formLoading = true
        const partialBooking = {
            clientKey: this.clientService.activeClient!.$key!,
            merchantKey: this.parking.merchantRef.id,
            parkingKey: this.parkingKey!,
            bayNumber: this.formData.value.bayNumber,
            startDate: this.formData.value.startDate,
        }

        this.bookingService.createBooking(partialBooking).then((response) => {
            this.formLoading = false
            // this.formData.patchValue({
            //     startDate: '',
            //     bayNumber: '',
            // })
        })
    }

    private drawBookingAreas(selectedDate?: string) {
        // console.log(this.parking.bookings)
        let baysBookedForThisDate: string[] = []
        if (this.parking.bookings) {
            Object.keys(this.parking.bookings).forEach((key) => {
                const parts = key.split('-bay-')
                if (parts.length !== 2) {
                    throw new Error('Invalid input format')
                }

                const bookingDate = parts[0]
                const bookingBayNumber = parts[1]

                if (!selectedDate) {
                    const currentDate = new Date()
                    selectedDate = moment(currentDate).format('YYYY-MM-DD')
                }

                if (selectedDate == bookingDate) {
                    baysBookedForThisDate.push(bookingBayNumber)
                }
            })
        }

        // Permanent
        if (this.parking.bookingsPermanent) {
            Object.keys(this.parking.bookingsPermanent).forEach((key) => {
                const parts = key.split('bay-')
                if (parts.length !== 2) {
                    throw new Error('Invalid input format')
                }

                const bookingBayNumber = parts[1]
                baysBookedForThisDate.push(bookingBayNumber)
            })
        }
        this.bookingAreas = this.globalService.convertImageMapToJSON(this.parking.parkingLayoutJson)

        this.bookingAreas.forEach((area: any) => {
            if (baysBookedForThisDate.includes(area.bayNumber)) {
                area.unavailable = true
            }
        })
    }

    closeSaveFormModal() {
        this.modalRef?.hide()
    }

    openImageModal(template: TemplateRef<any>) {
        this.modalRef = this.modalService.show(template, {
            class: 'modal-lg',
            backdrop: 'static',
            keyboard: false, // This will also prevent closing with the keyboard 'Esc' button
        })
    }

    getImageDimensions(imageUrl: string): void {
        const img = new Image()
        img.src = environment.host + '/' + imageUrl
        img.src = imageUrl
        console.log(img)

        img.addEventListener('load', () => {
            console.log(img)
            this.svgWidth = img.width
            this.svgHeight = img.height
        })
    }
}
